import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

type Cell = 'X' | 'O' | null;
type Board = Cell[];

const LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function winner(b: Board): Cell | 'draw' | null {
  for (const [a, c, d] of LINES) {
    if (b[a] && b[a] === b[c] && b[a] === b[d]) return b[a];
  }
  return b.every(Boolean) ? 'draw' : null;
}

// Minimax for the AI (player O). depthLimit caps look-ahead for easier levels.
function minimax(b: Board, isMax: boolean, depth: number, limit: number): number {
  const w = winner(b);
  if (w === 'O') return 10 - depth;
  if (w === 'X') return depth - 10;
  if (w === 'draw' || depth >= limit) return 0;

  let best = isMax ? -Infinity : Infinity;
  for (let i = 0; i < 9; i++) {
    if (!b[i]) {
      b[i] = isMax ? 'O' : 'X';
      const val = minimax(b, !isMax, depth + 1, limit);
      b[i] = null;
      best = isMax ? Math.max(best, val) : Math.min(best, val);
    }
  }
  return best;
}

const DIFFS = [
  { name: 'Easy', limit: 1, randomness: 0.5 },
  { name: 'Medium', limit: 3, randomness: 0.2 },
  { name: 'Hard', limit: 9, randomness: 0 },
];

function aiMove(b: Board, limit: number, randomness: number): number {
  const empties = b.map((c, i) => (c ? -1 : i)).filter(i => i >= 0);
  if (Math.random() < randomness) return empties[Math.floor(Math.random() * empties.length)];
  let bestVal = -Infinity;
  let bestMove = empties[0];
  for (const i of empties) {
    b[i] = 'O';
    const val = minimax(b, false, 0, limit);
    b[i] = null;
    if (val > bestVal) { bestVal = val; bestMove = i; }
  }
  return bestMove;
}

const GameTicTacToe: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('tictactoe');
  const [level, setLvl] = useState(prog.level || 1);
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [turn, setTurn] = useState<'X' | 'O'>('X');
  const [result, setResult] = useState<Cell | 'draw' | null>(null);
  const [streak, setStreak] = useState(0);
  const [best] = useState(getHighScore('tictactoe'));
  const savedRef = useRef(0);

  const diff = DIFFS[Math.min(level - 1, DIFFS.length - 1)];

  const newRound = useCallback((keepStreak: boolean) => {
    setBoard(Array(9).fill(null));
    setTurn('X');
    setResult(null);
    if (!keepStreak) setStreak(0);
  }, []);

  const play = (i: number) => {
    if (board[i] || result || turn !== 'X') return;
    const nb = board.slice();
    nb[i] = 'X';
    setBoard(nb);
    sfx.place();
    const w = winner(nb);
    if (w) { finish(w); return; }
    setTurn('O');
  };

  const finish = (w: Cell | 'draw') => {
    setResult(w);
    if (w === 'X') {
      sfx.win();
      setStreak(s => {
        const ns = s + 1;
        return ns;
      });
    } else {
      // a loss or draw ends the streak score-wise (record then reset)
      if (w === 'O') sfx.lose();
      setStreak(0);
    }
  };

  // AI turn.
  useEffect(() => {
    if (turn === 'O' && !result) {
      const id = setTimeout(() => {
        setBoard(prev => {
          const nb = prev.slice();
          const move = aiMove(nb.slice(), diff.limit, diff.randomness);
          nb[move] = 'O';
          const w = winner(nb);
          if (w) finish(w);
          else setTurn('X');
          return nb;
        });
      }, 320);
      return () => clearTimeout(id);
    }
  }, [turn, result, diff.limit, diff.randomness]);

  // Record best streak (points = streak). Unlock next difficulty after a win.
  useEffect(() => {
    if (streak > savedRef.current) {
      savedRef.current = streak;
      recordScore('tictactoe', streak, level);
      if (level < DIFFS.length) unlockNext('tictactoe', level);
    }
  }, [streak, level]);

  return (
    <div className="gttt">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{streak}</b><i>streak</i></span>
          <span className="gstat"><b>{Math.max(best, streak)}</b><i>best</i></span>
          <span className="gstat"><b>{diff.name}</b><i>mode</i></span>
        </div>
        <button className="game-restart" onClick={() => newRound(true)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {DIFFS.map((d, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('tictactoe').unlockedLevel;
          return (
            <button key={d.name} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => { if (unlocked) { setLvl(lvl); setLevel('tictactoe', lvl); newRound(false); } }}
              disabled={!unlocked} title={unlocked ? d.name : 'Win to unlock'}>
              {unlocked ? d.name : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> win streak</div>
      )}

      <div className="gttt-board">
        {board.map((c, i) => (
          <button key={i} className={`gttt-cell ${c ? `mark-${c}` : ''}`} onClick={() => play(i)} disabled={!!c || !!result}>
            {c}
          </button>
        ))}
      </div>

      <div className="gttt-status">
        {result === 'X' && <span className="win">You win! 🎉 Streak {streak}</span>}
        {result === 'O' && <span className="lose">Computer wins 🤖 — streak reset</span>}
        {result === 'draw' && <span className="draw">It’s a draw 🤝 — streak reset</span>}
        {!result && <span>{turn === 'X' ? 'Your move (X)' : 'Computer thinking…'}</span>}
      </div>

      {result && (
        <div className="go-actions" style={{ marginTop: 12 }}>
          <button className="g-btn primary" onClick={() => newRound(true)}>Play again</button>
          <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
        </div>
      )}

      <p className="game-hint">You are X. Win in a row to build a streak — each win is a point.</p>
    </div>
  );
};

export default GameTicTacToe;
